import styled from 'styled-components'
import { Handle } from '@xyflow/react'
import { SPACING, COLORS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import { NODE_DEFAULTS } from '../../../../utils/nodeDefaults'
import { getContrastingTextColor } from '../../../../utils/colorUtils'
import type React from 'react'

/**
 * Styled components for SummarizedNode component
 * Displays a collapsed hierarchical view of nodes
 */

// Wrapper for node positioning - sets CSS variable for zoom-independent scaling
export const NodeWrapper = styled.div.attrs<{ $zoomScale: number }>((props) => ({
  style: {
    '--zoom-scale': props.$zoomScale,
  } as React.CSSProperties,
}))`
  position: relative;
  display: inline-block;
`

// Styled container for the summarized node - modern gradient design
export const StyledSummarizedContainer = styled.div`
  /* Base with subtle left-edge gradient spanning full height */
  background:
    linear-gradient(
      to right,
      rgba(148, 163, 184, 0.08) 0%,
      rgba(148, 163, 184, 0.045) 20%,
      rgba(148, 163, 184, 0.018) 50%,
      transparent 75%
    ),
    var(--color-node-bg);
  border: 1px solid var(--color-node-border);
  border-radius: 8px;
  width: ${NODE_DEFAULTS.width}px;
  max-width: ${NODE_DEFAULTS.maxWidth + 80}px;
  min-height: 80px;
  max-height: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: grab;
  box-shadow: var(--shadow-node);
  transition: box-shadow 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    box-shadow: var(--shadow-node-hover);
  }

  /* Allow pinch-zoom on mobile while preserving single-finger drag */
  touch-action: manipulation;
`

// Category dot for showing node type
export const CategoryDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  flex-shrink: 0;
  margin-top: ${SPACING.xs};
`

// Label chip for showing tags on collapsed items
export const LabelChip = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  background-color: ${(props) => props.$color};
  color: ${(props) => getContrastingTextColor(props.$color)};
  max-width: 60px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
`

// Scrollable content area - uses CSS to handle scroll vs zoom properly
export const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${SPACING.md};
  background-color: transparent;
  overscroll-behavior: contain;
  /* Prevent browser pinch-to-zoom while allowing scroll */
  touch-action: pan-y;
  /* Allow text selection */
  user-select: text;
  cursor: default;
`

// Header actions group - uses CSS variable for zoom-independent scaling
// Higher cap (1.8) for buttons to stay clickable when zoomed out (matches BaseNode)
export const HeaderActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  transform: scale(min(var(--zoom-scale, 1), 1.8));
  transform-origin: right center;
`

// Header container - transparent to let gradient show
export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.sm} ${SPACING.md};
  border-bottom: 1px solid var(--color-node-header-border);
  background-color: transparent;
`

// Header left section - uses CSS variable for zoom-independent scaling
// Capped at 1.3 max to prevent headers from getting too large
export const HeaderLeftSection = styled.div`
  display: flex;
  align-items: center;
  transform: scale(min(var(--zoom-scale, 1), 1.3));
  transform-origin: left center;
`

// Header title - styling only, scaling handled by parent HeaderLeftSection
export const HeaderTitle = styled.span`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${600};
  line-height: ${TYPOGRAPHY.h4.lineHeight};
  color: ${COLORS.slate500};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: ${SPACING.sm};

  body[data-theme='dark'] & {
    color: rgba(255, 255, 255, 0.75);
  }
`

// Hierarchy item container
export const HierarchyItemContainer = styled.div`
  position: relative;
`

// Item content row
export const ItemContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm};
  padding: ${SPACING.xs} 0;
`

// Item text
export const ItemText = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-primary);
  line-height: 1.4;
  word-break: break-word;
  flex: 1;
  cursor: text;
`

// Label chips container
export const LabelChipsContainer = styled.div`
  display: flex;
  gap: 3px;
  flex-shrink: 0;
  align-items: center;
  margin-top: 1px;
`

// Children container with vertical line
export const ChildrenContainer = styled.div`
  position: relative;
  margin-left: 0.625rem;
`

// Vertical line
export const VerticalLine = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: var(--color-border-dark);
`

// Children list
export const ChildrenList = styled.div`
  padding-left: 1rem;
`

// Category indicator bar - subtle accent at bottom
export const CategoryIndicatorBar = styled.div<{ $color: string }>`
  height: 3px;
  background: linear-gradient(
    90deg,
    ${(props) => props.$color} 0%,
    ${(props) => props.$color}60 100%
  );
`

// Invisible handle styled component
const InvisibleHandle = styled(Handle)`
  opacity: 0;
  width: 16px;
  height: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`

// Target handle
export const TargetHandle = styled(InvisibleHandle)`
  left: -8px;
`

// Source handle
export const SourceHandle = styled(InvisibleHandle)`
  right: -8px;
`
